/**
 * Feriados nacionais do Brasil e dia útil. Só o calendário nacional: feriado
 * estadual e municipal fica de fora.
 *
 * Carnaval (segunda e terça) e Corpus Christi são ponto facultativo, não
 * feriado por lei; entram marcados como `facultativo` e, por padrão, não
 * contam como dia útil, porque banco não abre e conta não vence nesses dias.
 */
const DAY_MS = 24 * 60 * 60 * 1000;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
/** Consciência Negra virou feriado nacional em 2024 (Lei 14.759/2023). */
const CONSCIENCIA_NEGRA_DESDE = 2024;
const FIXOS = [
    { mes: 1, dia: 1, nome: 'Confraternização Universal' },
    { mes: 4, dia: 21, nome: 'Tiradentes' },
    { mes: 5, dia: 1, nome: 'Dia do Trabalho' },
    { mes: 9, dia: 7, nome: 'Independência do Brasil' },
    { mes: 10, dia: 12, nome: 'Nossa Senhora Aparecida' },
    { mes: 11, dia: 2, nome: 'Finados' },
    { mes: 11, dia: 15, nome: 'Proclamação da República' },
    { mes: 11, dia: 20, nome: 'Dia da Consciência Negra', desde: CONSCIENCIA_NEGRA_DESDE },
    { mes: 12, dia: 25, nome: 'Natal' },
];
function iso(ms) {
    return new Date(ms).toISOString().slice(0, 10);
}
function parse(data) {
    const match = ISO_DATE.exec(data);
    const ms = match
        ? Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
        : Number.NaN;
    if (Number.isNaN(ms) || iso(ms) !== data)
        throw new RangeError(`Data inválida: ${data}`);
    return ms;
}
/** Domingo de Páscoa do ano, "AAAA-MM-DD" (calendário gregoriano). */
export function pascoa(ano) {
    const a = ano % 19;
    const b = Math.floor(ano / 100);
    const c = ano % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const mes = Math.floor((h + l - 7 * m + 114) / 31);
    const dia = ((h + l - 7 * m + 114) % 31) + 1;
    return iso(Date.UTC(ano, mes - 1, dia));
}
const porAno = new Map();
/** Feriados nacionais e pontos facultativos do ano, em ordem de data. */
export function feriadosNacionais(ano) {
    if (!Number.isInteger(ano) || ano < 1900 || ano > 2399) {
        throw new RangeError(`Ano fora do calendário: ${ano}`);
    }
    let lista = porAno.get(ano);
    if (!lista) {
        const domingo = parse(pascoa(ano));
        const movel = (dias, nome, tipo) => ({
            data: iso(domingo + dias * DAY_MS),
            nome,
            tipo,
            movel: true,
        });
        lista = [
            ...FIXOS.filter((fixo) => fixo.desde === undefined || ano >= fixo.desde).map((fixo) => ({
                data: iso(Date.UTC(ano, fixo.mes - 1, fixo.dia)),
                nome: fixo.nome,
                tipo: 'nacional',
                movel: false,
            })),
            movel(-48, 'Carnaval', 'facultativo'),
            movel(-47, 'Carnaval', 'facultativo'),
            movel(-2, 'Sexta-feira Santa', 'nacional'),
            movel(60, 'Corpus Christi', 'facultativo'),
        ].sort((x, y) => x.data.localeCompare(y.data));
        porAno.set(ano, lista);
    }
    return lista.map((feriado) => ({ ...feriado }));
}
/** O feriado (ou ponto facultativo) daquela data, se houver. */
export function feriadoEm(data) {
    parse(data);
    return (feriadosNacionais(Number(data.slice(0, 4))).find((feriado) => feriado.data === data) ?? null);
}
/** Dia útil: de segunda a sexta e sem feriado nacional. */
export function ehDiaUtil(data, options = {}) {
    const diaDaSemana = new Date(parse(data)).getUTCDay();
    if (diaDaSemana === 0 || diaDaSemana === 6)
        return false;
    const feriado = feriadoEm(data);
    if (!feriado)
        return true;
    return feriado.tipo === 'facultativo' && options.facultativos === false;
}
/**
 * Para "adiar para o próximo dia útil": devolve a própria data quando ela já é
 * dia útil; senão, o primeiro dia útil depois dela.
 */
export function proximoDiaUtil(data, options = {}) {
    let ms = parse(data);
    while (!ehDiaUtil(iso(ms), options))
        ms += DAY_MS;
    return iso(ms);
}
